// One client guide, before the welcome tour. No automatic prompt without durable storage.
//
// 16 October 2026, the owner's decisions: the app is "Cipher" on the home screen; the guide comes
// first, before the welcome tour; someone who removed the icon is offered it again after a few days,
// not 30; and someone arriving from the old Nutrition address is told once that Cipher is now one app.
export const SHOWN_KEY = 'cipher_pwa_guide_shown_v1';
export const OLD_ADDRESS_KEY = 'cipher_old_address_note';
export const INSTALLED_KEY = 'cipher_installed_at';
export const COUNT_KEY = 'cipher_pwa_guide_count';
export const REMOVED_KEY = 'cipher_icon_removed_at';
const OLD_SEEN_KEY = 'cipher_old_address_seen';
export const SOON_DAYS = 3;
export const MAX_SHOWS = 3;
const DAY = 86400000;
export const guideStorage = () => { try { return window.localStorage; } catch { return null; } };
const get = (key, storage = guideStorage()) => { try { return storage?.getItem(key); } catch { return null; } };
let installedThisVisit = false;
let installOffer = null;
let tourHeld = null;
const offerListeners = new Set();
const tourListeners = new Set();
export const currentInstallOffer = () => installOffer;
export const onInstallOffer = (fn) => { offerListeners.add(fn); return () => offerListeners.delete(fn); };
export const hasInstalledRecord = () => installedThisVisit || !!get(INSTALLED_KEY);
export const markInstalled = (storage = guideStorage(), now = Date.now()) => {
  installedThisVisit = true;
  try { storage?.setItem(INSTALLED_KEY, String(now)); storage?.removeItem(REMOVED_KEY); } catch { /* no nag in this visit */ }
  installOffer = null;
  offerListeners.forEach(fn => fn(null));
};
// Only an explicit user report or a fresh browser install offer is evidence of removal.
export const markIconRemoved = (storage = guideStorage(), now = Date.now()) => {
  installedThisVisit = false;
  try { storage?.removeItem(INSTALLED_KEY); storage?.setItem(REMOVED_KEY, String(now)); } catch { /* automatic guide stays off */ }
};
export const setInstallOffer = offer => {
  if (offer && hasInstalledRecord()) markIconRemoved();
  installOffer = offer;
  offerListeners.forEach(fn => fn(offer));
};
export function detectDevice(nav = navigator) {
  const ua = nav.userAgent || '';
  if (/iPhone|iPod/.test(ua)) return 'iphone';
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && nav.maxTouchPoints > 1)) return 'ipad';
  if (/Android/.test(ua)) return 'android';
  return 'desktop';
}
export function detectBrowser(nav = navigator) {
  const ua = nav.userAgent || '';
  if (/Instagram|FBAN|FBAV|TikTok|musical_ly|Bytedance|WhatsApp|Gmail|GSA\/|; wv\)/i.test(ua)) return 'in-app';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/EdgiOS|EdgA|Edg\//.test(ua)) return 'edge';
  if (/FxiOS|Firefox\//.test(ua)) return 'firefox';
  if (/CriOS|Chrome\//.test(ua)) return 'chrome';
  if (/Version\/.*Safari\//.test(ua)) return 'safari';
  return 'unknown';
}
export function isStandalone(win = window) {
  try { return win.matchMedia('(display-mode: standalone)').matches || win.navigator.standalone === true; } catch { return false; }
}
export const readOldAddressMarker = (loc = window.location, storage = guideStorage(), history = window.history) => {
  try {
    const params = new URLSearchParams(loc.search);
    if (params.get('from') !== 'old-nutrition') return;
    if (!storage?.getItem(OLD_SEEN_KEY)) storage?.setItem(OLD_ADDRESS_KEY, '1');
    params.delete('from');
    history.replaceState(history.state, '', loc.pathname + (params.toString() ? `?${params}` : '') + loc.hash);
  } catch { /* no automatic prompt if storage is blocked */ }
};
export const cameFromOldAddress = () => get(OLD_ADDRESS_KEY) === '1';
export const acknowledgeOldAddress = () => {
  try { const s = guideStorage(); s?.removeItem(OLD_ADDRESS_KEY); s?.setItem(OLD_SEEN_KEY, '1'); } catch { /* optional notice */ }
};
export const installGuideDue = ({ device = detectDevice(), standalone = isStandalone(), native = false,
  role = 'client', blocked = false, now = Date.now(), storage = guideStorage() } = {}) => {
  if (role !== 'client' || blocked || native || standalone || device === 'desktop' || installedThisVisit) return false;
  try {
    if (!storage) return false;
    // A getter may work even when writes are blocked. Do not show a repeatable automatic prompt then.
    storage.setItem('cipher_guide_storage_check', '1'); storage.removeItem('cipher_guide_storage_check');
    if (storage.getItem(INSTALLED_KEY) || Number(storage.getItem(COUNT_KEY)) >= MAX_SHOWS) return false;
    const last = Math.max(Number(storage.getItem(SHOWN_KEY)), Number(storage.getItem(REMOVED_KEY)));
    return !last || now - last >= SOON_DAYS * DAY;
  } catch { return false; }
};
export const markInstallGuideShown = (storage = guideStorage(), now = Date.now()) => {
  try { storage?.setItem(SHOWN_KEY, String(now)); storage?.setItem(COUNT_KEY, String(Number(storage.getItem(COUNT_KEY) || 0) + 1)); } catch { /* automatic guide disabled without storage */ }
};
export const tourMayShow = () => {
  if (tourHeld === null) tourHeld = installGuideDue();
  return !tourHeld;
};
export const releaseTour = () => { tourHeld = false; tourListeners.forEach(fn => fn()); };
export const onTourReleased = fn => { tourListeners.add(fn); return () => tourListeners.delete(fn); };
export const resetHomeScreenGuide = () => { installOffer = null; tourHeld = null; installedThisVisit = false; };
if (typeof window !== 'undefined') {
  readOldAddressMarker();
  if (isStandalone()) markInstalled();
  window.addEventListener('beforeinstallprompt', e => { e.preventDefault(); if (!isStandalone()) setInstallOffer(e); });
  window.addEventListener('appinstalled', () => markInstalled());
}
